import { IKeyedObject, SingleOrArray } from '@dirootie-extensions/typescript';
import _ from 'lodash';
import { array2DimensionsToObject } from './array-to-object';
import { objectToPaths, toPath, toPathWithResult } from './object-to-paths';

export type PathResult = {
  value: any;
  path: string;
  parentKey: string;
  segments: string[];
};

export interface ObjectUtils {
  isDefined: (value: any) => boolean;
  getByPath: <T>(obj: T, path: string) => T;
  setByPath: <T>(obj: T, path: string, value: any) => T;
  array2DimensionsToObject: (
    arr: [any, any][],
    sndPositionHandler?: (key: any, param: any) => any
  ) => {};
  toArray: <T>(obj: IKeyedObject<T>) => T[];
  toArrayKvp: <T>(obj: IKeyedObject<T>) => { key: string; value: T }[];
  toPaths: <T>(obj: T, path?: string) => string[];
  toPath: <T>(
    obj: T,
    options?: { parentKey?: string; stopKey?: string }
  ) => string[];
  toPathWithResult: <T>(
    obj: any,
    options?: {
      parentKey?: string;
      stopKey?: SingleOrArray<string>;
      stopParentKey?: SingleOrArray<string>;
      onlyKey?: SingleOrArray<string>;
    }
  ) => PathResult[];
  fromObjectOrProperty: <T, TR>(obj: T, path: string) => TR;
  replacePropertyWithValue: <T extends object>(obj: T, path: string) => T;
}

const objectToArrayKvp = <T>(obj: IKeyedObject<T>) =>
  Object.keys(obj).map((key) => ({ key, value: obj[key] }));
const objectToArray = <T>(obj: IKeyedObject<T>) =>
  Object.keys(obj).map((key) => ({ ...obj[key] }));

const fromObjectOrProperty = <T>(obj: T, path: string) => {
  const result = _.get(obj, path);
  return result !== undefined ? result : obj;
};

const replacePropertyWithValue = <T extends object>(obj: T, path: string) => {
  return _.set(obj, path, _.get(obj, path));
};
const internalObjectUtils = (): ObjectUtils => {
  return {
    getByPath: <T>(obj: T, path: string) => _.get(obj, path),
    setByPath: _.set,
    array2DimensionsToObject,
    toArrayKvp: objectToArrayKvp,
    toArray: objectToArray,
    toPaths: objectToPaths,
    toPath: toPath,
    toPathWithResult,
    isDefined: (value: any): boolean => !_.isUndefined(value),
    fromObjectOrProperty,
    replacePropertyWithValue,
  };
};

export const objectUtils = internalObjectUtils();
